import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check, Minus, Plus, FileDown, Send, ArrowLeft, CalendarPlus, Edit2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TierEditorModal, CATEGORY_ORDER, CATEGORY_LABELS } from './VehicleTiersManager';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const MEMBERSHIP = [
  { label: 'Retail', value: 0, tag: '' },
  { label: 'Lobby', value: 0.08, tag: '8% off' },
  { label: 'Condo', value: 0.12, tag: '12% off' },
  { label: 'Penthouse', value: 0.18, tag: '18% off' },
];
const SERVICE_TYPES = [
  { value: 'AT', label: 'Airport Transfer' }, { value: 'PL', label: 'Prom / Event Luxury' },
  { value: 'CC', label: 'Corporate / Executive' }, { value: 'CO', label: 'Concert / Nightlife' },
  { value: 'LD', label: 'Long Distance' }, { value: 'WD', label: 'Wedding / Special Occasion' },
  { value: 'PS', label: 'Photo / Media Shoot' }, { value: 'OTHER', label: 'Other' },
];

const AIRPORT_PERIMETER = [
  { value: 'inside',  label: 'Inside Perimeter (I-285)',  price: 100, desc: 'Atlanta proper · Midtown · Buckhead · Downtown' },
  { value: 'outside', label: 'Outside Perimeter',          price: 150, desc: 'Alpharetta · Marietta · Sandy Springs · suburbs' },
];

const ADDON_PRESETS = [
  { label: 'Extra Stop', amt: 25 }, { label: 'Red Carpet Service', amt: 50 },
  { label: 'Champagne & Refreshments', amt: 75 }, { label: 'Airport Meet & Greet', amt: 50 },
];

const QuickQuoteTab = ({ prefillDate }) => {
  const [step, setStep] = useState(1);
  const [tierCats, setTierCats] = useState({});
  const [tiersLoading, setTiersLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [creatingCategory, setCreatingCategory] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [hours, setHours] = useState(4);
  const [membership, setMembership] = useState(0);
  const [form, setForm] = useState({ svcType: '', date: '', pickupTime: '', dropoffTime: '', flightNumber: '', pickup: '', dropoff: '', name: '', phone: '', email: '', notes: '' });
  const [addons, setAddons] = useState([]);
  const [customAddon, setCustomAddon] = useState({ label: '', amt: '' });
  const [perimeter, setPerimeter] = useState('inside');
  const [customPriceEnabled, setCustomPriceEnabled] = useState(false);
  const [customPrice, setCustomPrice] = useState('');
  const quoteRef = useRef(null);

  const loadTiers = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/vehicle-tiers`);
      const d = await r.json();
      setTierCats(d.categories || {});
    } catch { toast.error('Failed to load vehicle tiers'); }
    setTiersLoading(false);
  }, []);
  useEffect(() => { loadTiers(); }, [loadTiers]);

  const deleteTier = async (id, name) => {
    if (!window.confirm(`Delete tier "${name}"? It can be restored later.`)) return;
    try {
      const r = await fetch(`${API}/api/vehicle-tiers/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Tier removed');
      setEditingTier(null); setCreatingCategory(null);
      loadTiers();
    } catch (e) { toast.error(e.message); }
  };

  // Flatten tiers into quote-vehicle shape
  const allVehicles = CATEGORY_ORDER.flatMap(catKey =>
    ((tierCats[catKey]?.tiers) || []).map(t => ({
      id: t.tier_id,
      name: t.tier_name,
      retail: Number(t.hourly_rate) || 0,
      minHrs: Number(t.min_hours) || 1,
      paxN: Number(t.pax) || 0,
      pax: `Up to ${t.pax} passengers`,
      sub: (t.models || []).slice(0, 3).join(' · '),
      cat: t.category_label || CATEGORY_LABELS[catKey],
      catKey,
      image_url: t.image_url,
      _tier: t,
    }))
  );

  useEffect(() => {
    if (prefillDate) setForm(p => ({ ...p, date: prefillDate }));
  }, [prefillDate]);

  const v = selectedId !== null ? allVehicles.find(x => x.id === selectedId) : null;
  const discount = MEMBERSHIP[membership].value;
  const netRate = v ? Math.round(v.retail * (1 - discount)) : 0;

  // Pickup + drop-off time → auto hours (overnight wraps; rounds to half hour)
  useEffect(() => {
    if (!form.pickupTime || !form.dropoffTime) return;
    const [h1, m1] = form.pickupTime.split(':').map(Number);
    const [h2, m2] = form.dropoffTime.split(':').map(Number);
    let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (mins <= 0) mins += 1440;
    setHours(Math.max(Math.round(mins / 30) / 2, v?.minHrs || 1));
  }, [form.pickupTime, form.dropoffTime]); // eslint-disable-line

  // Airport Transfer = flat fee (Inside $100 / Outside $150) — overrides hourly
  const isAirport = form.svcType === 'AT';
  const airportFlat = isAirport ? AIRPORT_PERIMETER.find(p => p.value === perimeter)?.price || 0 : 0;
  // Manual price override always wins if enabled
  const customNum = Number(customPrice) || 0;
  const base = customPriceEnabled && customNum > 0
    ? customNum
    : isAirport
      ? airportFlat
      : (netRate * hours);
  const savings = !customPriceEnabled && !isAirport && v ? (v.retail * hours) - base : 0;
  const addonsTotal = addons.reduce((s, a) => s + (Number(a.amt) || 0), 0);
  const taxable = base + addonsTotal;
  const tax = Math.round(taxable * 0.08);
  const grat = Math.round(taxable * 0.12);
  const total = taxable + tax + grat;

  const selectVehicle = (t) => {
    setSelectedId(t.id);
    if (hours < t.minHrs) setHours(t.minHrs);
    setStep(2);
  };

  const goToQuote = () => {
    setStep(3);
  };

  const createBooking = async () => {
    const name = form.name.trim() || 'Guest';
    const date = form.date || new Date().toISOString().slice(0, 10);
    try {
      const res = await fetch(`${API}/api/bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerInfo: { firstName: name.split(' ')[0], lastName: name.split(' ').slice(1).join(' '), email: form.email || '', phone: form.phone || '' },
          serviceType: v.catKey === 'exotic_driven' ? 'exotic' : v.catKey === 'group_driven' ? 'group' : 'concierge',
          vehicleId: v.name, hours: isAirport ? 1 : hours, totalPrice: total,
          pickupDate: date, pickupTime: form.pickupTime || '12:00', pickupLocation: form.pickup || 'TBD', dropoffLocation: form.dropoff,
          specialRequests: [
            `${MEMBERSHIP[membership].label} rate`,
            SERVICE_TYPES.find(s => s.value === form.svcType)?.label,
            isAirport ? `Airport · ${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label} ($${airportFlat} flat)` : null,
            form.flightNumber ? `Flight ${form.flightNumber}` : null,
            form.dropoffTime ? `Drop-off ${form.dropoffTime}` : null,
            addons.length ? `Add-ons: ${addons.map(a => `${a.label} $${a.amt}`).join(', ')}` : null,
            customPriceEnabled ? `Custom price override: $${customNum}` : null,
            form.notes,
          ].filter(Boolean).join(' | '),
        }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Booking ${d.bookingId} created · Added to calendar`, { duration: 3500 });
        // Also save to quote log
        await fetch(`${API}/api/command-center/quote-log`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            vehicle_name: v.name, hours: isAirport ? 1 : hours, client_type: MEMBERSHIP[membership].label, client_name: name,
            client_phone: form.phone, client_email: form.email, event_date: date,
            service_type: form.svcType, base_price: base, addons, addons_total: addonsTotal, grand_total: total, status: 'confirmed',
          }),
        });
        // Send SMS via Quo
        if (form.phone) {
          await fetch(`${API}/api/command-center/quo/send-sms`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: form.phone,
              message: `ACE Booking Confirmed!\n\nHi ${name.split(' ')[0]}, your ${v.name} reservation is confirmed.\n\nDate: ${date}${form.pickupTime ? ` at ${form.pickupTime}` : ''}\n${isAirport ? `Airport Transfer (${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label})` : `Duration: ${hours} hours`}\nTotal: ${fmt(total)}\n\nQuestions? Reply here or call (678) 846-3009.\n— ACE Auto Concierge`,
            }),
          }).catch(() => {});
        }
      } else { toast.error('Failed to create booking'); }
    } catch { toast.error('Booking failed'); }
  };

  const sendQuoteViaSms = async () => {
    if (!form.phone) { toast.error('No phone number'); return; }
    try {
      await fetch(`${API}/api/command-center/quo/send-sms`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: form.phone,
          message: `ACE Quote for ${form.name}\n\nVehicle: ${v.name}\nDuration: ${hours} hrs\nRate: ${fmt(netRate)}/hr\nBase: ${fmt(base)}\nTax (8%): ${fmt(tax)}\nGratuity (12%): ${fmt(grat)}\nTotal: ${fmt(total)}${savings > 0 ? `\nMember Savings: ${fmt(savings)}` : ''}\n\nDate: ${form.date}\nPickup: ${form.pickup}\n\nReply YES to confirm or call (678) 846-3009.\n— ACE Auto Concierge`,
        }),
      });
      toast.success(`Quote sent to ${form.phone}`);
      // Log to quote log
      await fetch(`${API}/api/command-center/quote-log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicle_name: v.name, hours, client_type: MEMBERSHIP[membership].label, client_name: form.name,
          client_phone: form.phone, client_email: form.email, event_date: form.date,
          service_type: form.svcType, base_price: base, grand_total: total, status: 'sent',
        }),
      });
    } catch { toast.error('Failed to send'); }
  };

  const printQuote = () => {
    const w = window.open('', '_blank');
    const tierLabel = MEMBERSHIP[membership].label;
    const svcLabel = SERVICE_TYPES.find(s => s.value === form.svcType)?.label || '';
    w.document.write(`<html><head><title>ACE Quote</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:Georgia,serif;color:#111;max-width:620px;margin:40px auto;padding:20px;line-height:1.6}h1{font-size:22px;letter-spacing:3px;text-align:center;margin-bottom:4px}.sub{text-align:center;font-size:11px;color:#888;margin-bottom:24px}.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f0f0f0;font-size:13px}.row .lbl{color:#888}.row .val{font-weight:600;text-align:right}.total-row{border-top:2px solid #111;padding-top:10px;margin-top:4px;font-size:18px;font-weight:700}.total-row .val{color:#dc2626}.section{margin:20px 0 8px;font-size:10px;color:#888;text-transform:uppercase;letter-spacing:1px;font-weight:700}.save{background:#dc2626;color:#fff;text-align:center;padding:6px 16px;border-radius:4px;display:inline-block;margin-top:4px;font-size:10px;font-weight:700}.footer{margin-top:40px;text-align:center;font-size:10px;color:#999;border-top:1px solid #eee;padding-top:16px}.sig{margin-top:30px;font-size:12px}@media print{body{margin:20px}}</style></head><body>`);
    w.document.write(`<h1>ACE</h1><p class="sub">Auto Concierge Enterprises — Service Quote</p>`);
    w.document.write(`<div class="section">CLIENT</div><div class="row"><span class="lbl">Name</span><span class="val">${form.name}</span></div><div class="row"><span class="lbl">Phone</span><span class="val">${form.phone}</span></div>`);
    w.document.write(`<div class="section">SERVICE</div><div class="row"><span class="lbl">Vehicle</span><span class="val">${v.name}</span></div><div class="row"><span class="lbl">Type</span><span class="val">${svcLabel}${isAirport ? ` · ${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label}` : ''}</span></div><div class="row"><span class="lbl">Date</span><span class="val">${form.date}${form.pickupTime ? ` at ${form.pickupTime}` : ''}</span></div>${isAirport ? `<div class="row"><span class="lbl">Trip</span><span class="val">One-way airport transfer</span></div>` : `<div class="row"><span class="lbl">Duration</span><span class="val">${hours} hours</span></div>`}<div class="row"><span class="lbl">Pickup</span><span class="val">${form.pickup}</span></div>${form.dropoff ? `<div class="row"><span class="lbl">Drop-off</span><span class="val">${form.dropoff}</span></div>` : ''}<div class="row"><span class="lbl">Membership</span><span class="val">${tierLabel}</span></div>`);
    w.document.write(`<div class="section">PRICING</div><div class="row"><span class="lbl">${isAirport ? `${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label} (flat)` : customPriceEnabled && customNum > 0 ? 'Custom Base Price' : `Rate (${fmt(netRate)}/hr × ${hours})`}</span><span class="val">${fmt(base)}</span></div>`);
    if (savings > 0) w.document.write(`<div class="row"><span class="lbl">Member Savings</span><span class="val" style="color:#059669">−${fmt(savings)}</span></div>`);
    w.document.write(`<div class="row"><span class="lbl">Sales Tax (8%)</span><span class="val">${fmt(tax)}</span></div><div class="row"><span class="lbl">Gratuity (12%)</span><span class="val">${fmt(grat)}</span></div>`);
    w.document.write(`<div class="row total-row"><span class="lbl">Total Estimate</span><span class="val">${fmt(total)}</span></div>`);
    if (form.notes) w.document.write(`<div class="section">NOTES</div><p style="font-size:12px;color:#555">${form.notes}</p>`);
    w.document.write(`<p style="font-size:10px;color:#888;margin-top:20px">This quote is valid for 48 hours. Final pricing depends on date, distance, availability, and membership tier. A 50% deposit is required to secure your booking. Gratuity is suggested at 12%. Cancellations within 24 hours are subject to a late cancel fee.</p>`);
    w.document.write(`<div class="sig"><p>Client Signature: ________________________ Date: ____________</p></div>`);
    w.document.write(`<div class="footer"><p><strong>ACE — Auto Concierge Enterprises</strong></p><p>(678) 846-3009 | JD@YourAutoConcierge.com | YourAutoConcierge.com</p></div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const reset = () => {
    setStep(1); setSelectedId(null); setHours(4); setMembership(0);
    setForm({ svcType: '', date: '', pickupTime: '', pickup: '', dropoff: '', name: '', phone: '', email: '', notes: '' });
    setPerimeter('inside'); setCustomPriceEnabled(false); setCustomPrice('');
  };

  // Step indicators
  const StepBar = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[{ n: 1, l: 'Vehicle' }, { n: 2, l: 'Details' }, { n: 3, l: 'Your Quote' }].map((s, i) => (
        <React.Fragment key={s.n}>
          {i > 0 && <div className={`h-px w-8 ${step >= s.n ? 'bg-red-600' : 'bg-gray-300'}`} />}
          <button onClick={() => { if (s.n < step || (s.n === 2 && selectedId !== null) || (s.n === 3 && form.name)) setStep(s.n); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${step === s.n ? 'bg-red-600 text-white' : step > s.n ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`}>
            <span className="h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-current/10">{step > s.n ? <Check className="h-3 w-3" /> : s.n}</span> {s.l}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="qq-ops space-y-4 max-w-4xl mx-auto rounded-sm" data-testid="quick-quote-tab">
      {/* Ops-style masthead */}
      <div className="qq-masthead flex items-center justify-between px-5 py-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-2xl" style={{ color: '#C8102E' }}>♠</span>
          <div>
            <h2 className="qq-bebas text-3xl leading-none tracking-wide" data-testid="qq-title">GET A QUOTE</h2>
            <p className="qq-cond text-[10px] uppercase tracking-[0.3em] text-gray-500">Auto Concierge Enterprises · Real Pricing · No Surprises</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {['Members Save', '4-Hour Minimum', '8% Tax · 12% Gratuity Incl.'].map((b, i) => (
            <span key={i} className="qq-cond text-[10px] uppercase tracking-widest border border-[#2c2c2c] text-gray-400 px-2.5 py-1">{b}</span>
          ))}
        </div>
      </div>

      <div className="px-5 pb-5">
      <StepBar />

      {/* ===== STEP 1: VEHICLE (tier thumbnails) ===== */}
      {step === 1 && (
        <div data-testid="step-vehicle">
          <p className="qq-cond text-xs uppercase tracking-[0.25em] text-gray-500 border-b border-[#2c2c2c] pb-2 mb-3"><span style={{ color: '#C8102E' }}>♠</span> Step 1 — Fleet Selection</p>
          {tiersLoading && <p className="text-xs text-gray-400 text-center py-8">Loading fleet tiers…</p>}
          {!tiersLoading && CATEGORY_ORDER.map(catKey => {
            const tiers = allVehicles.filter(t => t.catKey === catKey);
            if (tiers.length === 0) return null;
            return (
              <div key={catKey} className="mb-5" data-testid={`tier-group-${catKey}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold text-gray-700 uppercase tracking-[0.2em]">{CATEGORY_LABELS[catKey]} <span className="text-gray-400 normal-case">· {tiers.length} tier{tiers.length === 1 ? '' : 's'}</span></p>
                  <button
                    onClick={() => setCreatingCategory(catKey)}
                    className="text-[10px] text-red-600 font-bold uppercase tracking-wider flex items-center gap-1 hover:text-red-700"
                    data-testid={`tier-add-${catKey}`}
                  ><Plus className="h-3 w-3" /> Add Tier</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {tiers.map(t => {
                    const sel = selectedId === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => selectVehicle(t)}
                        className={`text-left rounded-md overflow-hidden group border-2 transition-all ${sel ? 'border-red-600 bg-red-50 shadow-md' : 'border-gray-200 bg-gray-50 hover:border-red-300 hover:bg-gray-100'}`}
                        data-testid={`tier-card-${t.id}`}
                      >
                        <div className="aspect-[4/3] bg-black overflow-hidden relative">
                          {t.image_url ? (
                            <img src={t.image_url} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-600"><ImageIcon className="h-8 w-8" /></div>
                          )}
                          {sel && <div className="absolute top-2 left-2 h-6 w-6 rounded-full bg-red-600 flex items-center justify-center"><Check className="h-3.5 w-3.5 text-white" /></div>}
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditingTier(t._tier); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setEditingTier(t._tier); } }}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            title="Edit tier (rate, image, models)"
                            data-testid={`tier-edit-${t.id}`}
                          ><Edit2 className="h-3.5 w-3.5 text-white" /></span>
                        </div>
                        <div className="p-2.5">
                          <p className="text-xs font-bold text-gray-900 truncate">{t.name}</p>
                          <p className="text-sm font-black text-red-600">{fmt(t.retail)}<span className="text-[10px] text-gray-400 font-normal">/hr</span></p>
                          <p className="text-[10px] text-gray-500">{t.paxN} pax · {t.minHrs}h min</p>
                          {t.sub && <p className="text-[10px] text-gray-400 mt-0.5 truncate" title={t.sub}>{t.sub}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tier editor (admin) */}
      {(editingTier || creatingCategory) && (
        <TierEditorModal
          tier={editingTier}
          newCategory={creatingCategory}
          onClose={() => { setEditingTier(null); setCreatingCategory(null); }}
          onSaved={() => { setEditingTier(null); setCreatingCategory(null); loadTiers(); }}
          onDelete={deleteTier}
        />
      )}

      {/* ===== STEP 2: DETAILS ===== */}
      {step === 2 && v && (
        <div data-testid="step-details">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setStep(1)} className="p-1 rounded hover:bg-gray-100"><ArrowLeft className="h-4 w-4 text-gray-500" /></button>
            <p className="qq-cond text-xs uppercase tracking-[0.25em] text-gray-500"><span style={{ color: '#C8102E' }}>♠</span> Step 2 — Client &amp; Event Details</p>
          </div>

          {/* Selected vehicle badge */}
          <Card className="p-3 mb-4 bg-gray-950 text-white border-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {v.image_url && <img src={v.image_url} alt={v.name} className="h-12 w-16 object-cover rounded flex-shrink-0" />}
                <div className="min-w-0"><p className="text-xs text-gray-400">Selected Vehicle</p><p className="text-sm font-bold truncate">{v.name}</p></div>
              </div>
              <p className="text-lg font-black flex-shrink-0">{fmt(v.retail)}<span className="text-xs text-gray-400 font-normal">/hr</span></p>
            </div>
          </Card>

          {/* Membership */}
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">ACE Member Discount</p>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {MEMBERSHIP.map((m, i) => (
              <button key={i} onClick={() => setMembership(i)}
                className={`p-2 rounded-lg border text-center transition-all ${membership === i ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid={`mem-${i}`}>
                <p className={`text-xs font-bold ${membership === i ? 'text-red-600' : 'text-gray-700'}`}>{m.label}</p>
                {m.tag && <p className="text-[9px] text-gray-500">{m.tag}</p>}
              </button>
            ))}
          </div>

          {/* Hours stepper — hidden for Airport Transfer (flat fee) */}
          {!isAirport && (
            <>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Duration</p>
              <div className="flex items-center gap-3 mb-1">
                <button onClick={() => setHours(h => Math.max(v.minHrs, h - 1))} className="h-10 w-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="hrs-minus"><Minus className="h-4 w-4" /></button>
                <span className="text-3xl font-black w-16 text-center" data-testid="hrs-value">{hours}</span>
                <button onClick={() => setHours(h => Math.min(24, h + 1))} className="h-10 w-10 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50" data-testid="hrs-plus"><Plus className="h-4 w-4" /></button>
                <span className="text-sm text-gray-400 ml-2">hours</span>
              </div>
              <p className="text-[10px] text-gray-400 mb-4">{v.minHrs}-hour minimum applies · All-day rate (8+ hrs) available upon request</p>
            </>
          )}

          {/* Service fields */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Service Type</label>
              <select value={form.svcType} onChange={e => setForm(p => ({ ...p, svcType: e.target.value }))} className="w-full h-9 text-xs border rounded-lg px-3 border-gray-200" data-testid="svc-type">
                <option value="">Select...</option>{SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Date Needed</label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="h-9 text-xs" data-testid="svc-date" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Pickup Time</label>
              <Input type="time" value={form.pickupTime} onChange={e => setForm(p => ({ ...p, pickupTime: e.target.value }))} className="h-9 text-xs" data-testid="svc-pickup-time" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Drop-off Time <span className="text-gray-400 normal-case">(auto-calcs hours)</span></label>
              <Input type="time" value={form.dropoffTime} onChange={e => setForm(p => ({ ...p, dropoffTime: e.target.value }))} className="h-9 text-xs" data-testid="svc-dropoff-time" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Pick-up Location</label>
              <Input value={form.pickup} onChange={e => setForm(p => ({ ...p, pickup: e.target.value }))} placeholder="Address or venue" className="h-9 text-xs" data-testid="pickup" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Drop-off Location</label>
              <Input value={form.dropoff} onChange={e => setForm(p => ({ ...p, dropoff: e.target.value }))} placeholder="Address or venue" className="h-9 text-xs" data-testid="dropoff" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Client Name</label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="h-9 text-xs" data-testid="client-name" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Flight # <span className="text-gray-400 normal-case">(airport pickups)</span></label>
              <Input value={form.flightNumber} onChange={e => setForm(p => ({ ...p, flightNumber: e.target.value }))} placeholder="e.g. DL 1247" className="h-9 text-xs" data-testid="flight-number" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Phone Number <span className="text-gray-400 normal-case">(optional)</span></label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="(404) 555-1234" className="h-9 text-xs" data-testid="client-phone" /></div>
            <div><label className="text-[10px] font-bold text-gray-500 uppercase">Email <span className="text-gray-400 normal-case">(optional)</span></label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="client@example.com" className="h-9 text-xs" data-testid="client-email" /></div>
          </div>

          {/* Airport perimeter selector */}
          {isAirport && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="airport-perimeter-section">
              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-2">Airport Transfer · Pickup / Drop-off Zone</p>
              <div className="grid grid-cols-2 gap-2">
                {AIRPORT_PERIMETER.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPerimeter(opt.value)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      perimeter === opt.value
                        ? 'border-red-600 bg-white shadow-md'
                        : 'border-gray-200 bg-white hover:border-red-300'
                    }`}
                    data-testid={`perimeter-${opt.value}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-black text-gray-900">{opt.label}</p>
                      <p className="text-lg font-black text-red-600">${opt.price}</p>
                    </div>
                    <p className="text-[10px] text-gray-500 leading-relaxed">{opt.desc}</p>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Flat fee · One-way · Includes meet-&-greet</p>
            </div>
          )}

          {/* Add-on fees & services */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="addons-section">
            <p className="text-xs font-bold text-gray-900 mb-2">Additional Fees &amp; Services</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {ADDON_PRESETS.map(p => {
                const active = addons.some(a => a.label === p.label);
                return (
                  <button key={p.label} type="button"
                    onClick={() => setAddons(prev => active ? prev.filter(a => a.label !== p.label) : [...prev, { ...p }])}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-red-400'}`}
                    data-testid={`addon-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    {p.label} +${p.amt}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Input value={customAddon.label} onChange={e => setCustomAddon(c => ({ ...c, label: e.target.value }))} placeholder="Custom fee / service" className="h-8 text-xs flex-1" data-testid="addon-custom-label" />
              <Input type="number" min="0" value={customAddon.amt} onChange={e => setCustomAddon(c => ({ ...c, amt: e.target.value }))} placeholder="$" className="h-8 text-xs w-24" data-testid="addon-custom-amt" />
              <Button type="button" size="sm" variant="outline" className="h-8 text-xs"
                onClick={() => { if (customAddon.label && Number(customAddon.amt) > 0) { setAddons(p => [...p, { label: customAddon.label, amt: Number(customAddon.amt) }]); setCustomAddon({ label: '', amt: '' }); } }}
                data-testid="addon-custom-add">Add</Button>
            </div>
            {addons.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="addons-list">
                {addons.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] text-gray-600">
                    <span>{a.label}</span>
                    <span className="flex items-center gap-2 font-bold">{fmt(a.amt)}
                      <button onClick={() => setAddons(p => p.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-600">✕</button>
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-[11px] font-black text-gray-900 border-t pt-1"><span>Add-ons Total</span><span data-testid="addons-total">{fmt(addonsTotal)}</span></div>
              </div>
            )}
          </div>

          {/* Custom Price Override */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="custom-price-section">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-xs font-bold text-gray-900">Customize Price</p>
                <p className="text-[10px] text-gray-500">Override the calculated base price (admin only)</p>
              </div>
              <input
                type="checkbox"
                checked={customPriceEnabled}
                onChange={e => setCustomPriceEnabled(e.target.checked)}
                className="h-4 w-4 accent-red-600"
                data-testid="custom-price-toggle"
              />
            </label>
            {customPriceEnabled && (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm font-bold text-gray-500">$</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={customPrice}
                  onChange={e => setCustomPrice(e.target.value)}
                  placeholder="Enter custom base price"
                  className="h-9 text-xs flex-1"
                  data-testid="custom-price-input"
                />
                <span className="text-[10px] text-gray-400">Tax & gratuity will be added</span>
              </div>
            )}
          </div>

          <div className="mb-4"><label className="text-[10px] font-bold text-gray-500 uppercase">Special Requests / Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any special requirements, stops, or details..." rows={2} className="w-full text-xs border rounded-lg px-3 py-2 resize-none border-gray-200" data-testid="notes" /></div>

          <Button onClick={goToQuote} className="w-full bg-red-600 hover:bg-red-700 text-white h-11 text-sm font-bold" data-testid="get-quote-btn">
            See My Quote
          </Button>
        </div>
      )}

      {/* ===== STEP 3: QUOTE ===== */}
      {step === 3 && v && (
        <div ref={quoteRef} data-testid="step-quote">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setStep(2)} className="p-1 rounded hover:bg-gray-100"><ArrowLeft className="h-4 w-4 text-gray-500" /></button>
            <p className="qq-cond text-xs uppercase tracking-[0.25em] text-gray-500"><span style={{ color: '#C8102E' }}>♠</span> Step 3 — Your Quote</p>
          </div>

          <Card className="border-gray-200 overflow-hidden">
            {/* Quote header */}
            <div className="bg-gray-950 text-white p-5">
              <p className="text-lg font-black">{v.name}</p>
              <p className="text-xs text-gray-400">
                {isAirport
                  ? `Airport Transfer · ${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label}`
                  : `${hours} hours · ${MEMBERSHIP[membership].label} · ${SERVICE_TYPES.find(s => s.value === form.svcType)?.label || ''}`}
                {customPriceEnabled && customNum > 0 && <span className="ml-2 text-[10px] uppercase tracking-widest bg-red-600/30 px-1.5 py-0.5 rounded text-red-300 font-bold">Custom Price</span>}
              </p>
            </div>

            <div className="p-5 space-y-5">
              {/* Big numbers */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-black text-red-600" data-testid="q-total">{fmt(total)}</p>
                  <p className="text-[9px] text-gray-400 uppercase">Estimated Total</p>
                  <p className="text-[9px] text-gray-400">Incl. tax & gratuity</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-gray-900" data-testid="q-rate">{isAirport ? fmt(airportFlat) : fmt(netRate)}</p>
                  <p className="text-[9px] text-gray-400 uppercase">{isAirport ? 'Flat Fee' : 'Rate per Hour'}</p>
                  <p className="text-[9px] text-gray-400">
                    {isAirport ? 'One-way · meet & greet' : (discount > 0 ? 'After member discount' : 'Retail rate')}
                  </p>
                </div>
                <div>
                  <p className="text-2xl font-black text-gray-900">{isAirport ? '1' : hours}</p>
                  <p className="text-[9px] text-gray-400 uppercase">{isAirport ? 'Trip' : 'Hours'}</p>
                  <p className="text-[9px] text-gray-400">{isAirport ? 'One-way transfer' : `${v.minHrs}-hr minimum`}</p>
                </div>
              </div>

              {/* Savings badge */}
              {addons.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1" data-testid="quote-addons">
                  {addons.map((a, i) => (
                    <div key={i} className="flex justify-between text-xs text-gray-600"><span>{a.label}</span><span className="font-bold">{fmt(a.amt)}</span></div>
                  ))}
                </div>
              )}
              {savings > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-700">Member Savings</span>
                  <span className="text-lg font-black text-emerald-600" data-testid="savings">{fmt(savings)}</span>
                </div>
              )}

              {/* Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">
                    {isAirport
                      ? `${AIRPORT_PERIMETER.find(p => p.value === perimeter)?.label} (flat)`
                      : customPriceEnabled && customNum > 0
                        ? 'Custom Base Price (admin override)'
                        : `Base Rate (${fmt(netRate)}/hr × ${hours})`}
                  </span>
                  <span className="font-semibold">{fmt(base)}</span>
                </div>
                {savings > 0 && <div className="flex justify-between text-xs"><span className="text-emerald-600">Member Discount</span><span className="font-semibold text-emerald-600">−{fmt(savings)}</span></div>}
                <div className="flex justify-between text-xs"><span className="text-gray-500">Subtotal</span><span className="font-semibold">{fmt(base)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Sales Tax (8%)</span><span className="font-semibold">{fmt(tax)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-500">Gratuity (12%)</span><span className="font-semibold">{fmt(grat)}</span></div>
                <div className="flex justify-between text-sm border-t-2 border-gray-900 pt-2"><span className="font-bold">Total Estimate</span><span className="font-black text-red-600 text-lg">{fmt(total)}</span></div>
              </div>

              {/* Client info */}
              <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Client</span><span className="font-semibold">{form.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-semibold">{form.phone}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-semibold">{form.date}{form.pickupTime ? ` · ${form.pickupTime}` : ''}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Pickup</span><span className="font-semibold">{form.pickup}</span></div>
                {form.dropoff && <div className="flex justify-between"><span className="text-gray-500">Drop-off</span><span className="font-semibold">{form.dropoff}</span></div>}
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={createBooking} className="bg-red-600 hover:bg-red-700 text-white text-xs h-10 font-bold" data-testid="request-booking-btn">
                  <CalendarPlus className="h-3.5 w-3.5 mr-1.5" /> Book & Add to Calendar
                </Button>
                <Button onClick={printQuote} variant="outline" className="text-xs h-10 font-bold" data-testid="print-quote-btn">
                  <FileDown className="h-3.5 w-3.5 mr-1" /> Save / Print Quote
                </Button>
              </div>
              <Button onClick={sendQuoteViaSms} variant="outline" className="w-full text-xs h-9" data-testid="send-quote-sms-btn">
                <Send className="h-3.5 w-3.5 mr-1" /> Send Quote via SMS
              </Button>
              <Button onClick={reset} variant="ghost" className="w-full text-xs text-gray-400">
                Start New Quote
              </Button>

              {/* Fine print */}
              <p className="text-[9px] text-gray-400 leading-relaxed">All rates are estimates. Final pricing depends on date, distance, availability, add-ons, and membership tier. A 50% deposit is required to secure your booking. Gratuity is suggested at 12% and not included in driver pay. Cancellations within 24 hours of service are subject to a late cancel fee. Rates subject to change without notice. Lobby, Condo & Penthouse members receive tiered discounts as indicated above.</p>
            </div>
          </Card>

          {/* Rate Reference */}
          <Card className="mt-4 border-gray-200 p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Rate Reference</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-gray-200">
                  <th className="text-left py-1.5 text-[9px] text-gray-500 uppercase">Vehicle</th>
                  <th className="text-center py-1.5 text-[9px] text-gray-500 uppercase w-10">Pax</th>
                  <th className="text-center py-1.5 text-[9px] text-gray-500 uppercase w-16">/hr</th>
                  <th className="text-center py-1.5 text-[9px] text-gray-500 uppercase w-20">4-hr min</th>
                  <th className="text-center py-1.5 text-[9px] text-gray-500 uppercase w-20">8-hr day</th>
                </tr></thead>
                <tbody>
                  {CATEGORY_ORDER.map(catKey => (
                    <React.Fragment key={catKey}>
                      <tr><td colSpan={5} className="pt-3 pb-1 text-[9px] font-bold text-red-600 uppercase tracking-wider">{CATEGORY_LABELS[catKey]}</td></tr>
                      {allVehicles.filter(veh => veh.catKey === catKey).map((veh, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-1.5 font-semibold">{veh.name}</td>
                          <td className="py-1.5 text-center text-gray-500">{veh.paxN || '—'}</td>
                          <td className="py-1.5 text-center font-semibold">{fmt(veh.retail)}</td>
                          <td className="py-1.5 text-center">{fmt(veh.retail * 4)}</td>
                          <td className="py-1.5 text-center">{fmt(veh.retail * 8)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
      </div>

      {/* Ops-style sticky totals bar */}
      {v && step >= 2 && (
        <div className="qq-toolbar sticky bottom-0 z-20 flex items-center justify-between gap-3 px-5 py-3 flex-wrap" data-testid="qq-sticky-totals">
          <div className="flex items-center gap-5 flex-wrap">
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Grand Total</p>
              <p className="qq-bebas text-2xl leading-none" style={{ color: '#C8102E' }}>{fmt(total)}</p></div>
            <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Deposit (50%)</p>
              <p className="qq-bebas text-2xl leading-none text-white">{fmt(Math.round(total / 2))}</p></div>
            {addonsTotal > 0 && <div><p className="qq-cond text-[9px] uppercase tracking-[0.25em] text-gray-500">Add-ons</p>
              <p className="qq-bebas text-2xl leading-none text-gray-300">{fmt(addonsTotal)}</p></div>}
          </div>
          <div className="flex gap-2">
            {step === 2 && <Button onClick={goToQuote} size="sm" className="bg-red-700 hover:bg-red-600 h-9 text-xs qq-cond uppercase tracking-widest" data-testid="qq-bar-see-quote">See My Quote →</Button>}
            {step === 3 && <Button onClick={createBooking} size="sm" className="bg-red-700 hover:bg-red-600 h-9 text-xs qq-cond uppercase tracking-widest" data-testid="qq-bar-book">Book &amp; Add to Calendar</Button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuoteTab;
